<!-- Live Classes Showcase Section -->
<section
  id="live-classes"
  class="py-12 sm:py-20 lg:py-24 border-t transition-colors relative overflow-hidden"
  [ngClass]="theme === 'warm' ? 'bg-[#faf6f0] border-stone-200 text-stone-900' : (theme === 'bold' ? 'bg-slate-950 text-white border-blue-950' : 'bg-slate-900 text-white border-slate-800')"
  aria-labelledby="live-classes-heading"
>
  <!-- Background Glow -->
  @if (theme === 'modern') {
    <div class="absolute -top-40 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
  } @else if (theme === 'warm') {
    <div class="absolute -top-40 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
  } @else {
    <div class="absolute -top-40 right-0 w-96 h-96 bg-red-600/15 rounded-full blur-3xl pointer-events-none"></div>
  }

  <div class="container mx-auto px-4 sm:px-6 relative z-10 max-w-7xl">
    
    <!-- Section Header -->
    <div class="text-center max-w-3xl mx-auto mb-10 sm:mb-16 space-y-3 sm:space-y-4">
      <div
        class="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
        [ngClass]="theme === 'warm' ? 'bg-amber-500/15 text-amber-900 border border-amber-400/40' : (theme === 'bold' ? 'bg-red-600/20 text-red-300 border border-red-500/40' : 'bg-blue-500/15 text-blue-300 border border-blue-400/30')"
      >
        <i class="fa-solid fa-video text-xs"></i>
        <span>Live Batches</span>
      </div>

      <h2
        id="live-classes-heading"
        class="text-2xl sm:text-4xl font-extrabold tracking-tight break-words px-2"
        [ngClass]="theme === 'warm' ? 'text-stone-900' : 'text-white'"
      >
        {{ sectionTitle }}
      </h2>

      <p
        class="text-sm sm:text-lg leading-relaxed max-w-2xl mx-auto px-2"
        [ngClass]="theme === 'warm' ? 'text-stone-600' : 'text-slate-300'"
      >
        {{ sectionSubtitle }}
      </p>
    </div>

    <!-- Loading Skeletons -->
    @if (loading) {
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        @for (item of [1, 2, 3]; track item) {
          <div class="daisy-card bg-base-100/10 border border-base-content/10 p-5 rounded-2xl animate-pulse space-y-4">
            <div class="h-44 bg-base-content/10 rounded-xl"></div>
            <div class="h-5 bg-base-content/10 rounded w-3/4"></div>
            <div class="h-4 bg-base-content/10 rounded w-1/2"></div>
            <div class="h-10 bg-base-content/10 rounded-xl"></div>
          </div>
        }
      </div>
    }

    <!-- Live Classes Cards Grid -->
    @if (!loading && liveClasses.length > 0) {
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 sm:mb-12">
        @for (item of liveClasses; track item.id) {
          <div
            class="daisy-card border rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
            [ngClass]="theme === 'warm' ? 'bg-white border-stone-200 shadow-stone-300/30 text-stone-900 hover:border-amber-400' : (theme === 'bold' ? 'bg-slate-900 border-slate-800 shadow-blue-950/40 text-white hover:border-red-500/50' : 'bg-slate-800/90 border-slate-700/80 shadow-slate-950/40 text-white hover:border-blue-500/50')"
          >
            <!-- Banner Image or Gradient Fallback -->
            <div class="relative h-44 sm:h-48 w-full overflow-hidden bg-slate-950 flex items-center justify-center">
              @if (item.bannerImageUrl) {
                <img
                  [src]="item.bannerImageUrl"
                  [alt]="item.title"
                  class="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              } @else {
                <div
                  class="w-full h-full flex flex-col items-center justify-center text-center p-6"
                  [ngClass]="theme === 'warm' ? 'bg-gradient-to-br from-amber-500/20 to-stone-200 text-stone-800' : (theme === 'bold' ? 'bg-gradient-to-br from-blue-900/60 to-red-900/40 text-white' : 'bg-gradient-to-br from-blue-900/50 to-indigo-950 text-white')"
                >
                  <i class="fa-solid fa-graduation-cap text-4xl mb-2 opacity-60"></i>
                  <span class="font-bold text-sm line-clamp-1">{{ item.subject || 'Live Lecture Batch' }}</span>
                </div>
              }

              <!-- Badges Overlay -->
              <div class="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none gap-2">
                <span
                  class="daisy-badge daisy-badge-sm font-semibold shadow-sm shrink-0"
                  [ngClass]="theme === 'warm' ? 'daisy-badge-warning text-stone-900' : (theme === 'bold' ? 'daisy-badge-error text-white font-bold' : 'daisy-badge-primary text-white')"
                >
                  <i class="fa-solid fa-circle text-[6px] mr-1 animate-pulse"></i> Zoom Live
                </span>

                @if (item.targetAudience) {
                  <span class="daisy-badge daisy-badge-neutral daisy-badge-sm bg-black/60 backdrop-blur-sm text-white border-none font-medium text-[10px] truncate max-w-[150px]">
                    {{ item.targetAudience }}
                  </span>
                }
              </div>
            </div>

            <!-- Card Content Body -->
            <div class="p-4 sm:p-6 flex-1 flex flex-col justify-between space-y-4">
              <div>
                <!-- Subject / Medium Pill -->
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  @if (item.subject) {
                    <span
                      class="text-xs font-bold uppercase tracking-wider"
                      [ngClass]="theme === 'warm' ? 'text-amber-700' : (theme === 'bold' ? 'text-red-400' : 'text-blue-400')"
                    >
                      {{ item.subject }}
                    </span>
                  }
                  @if (item.medium) {
                    <span class="text-[11px] opacity-60">• {{ item.medium }} Medium</span>
                  }
                </div>

                <h3
                  class="text-base sm:text-lg font-bold line-clamp-2 leading-snug"
                  [ngClass]="theme === 'warm' ? 'text-stone-900' : 'text-white'"
                >
                  {{ item.title }}
                </h3>

                @if (item.descriptionSummary) {
                  <p
                    class="text-xs leading-relaxed line-clamp-2 mt-2"
                    [ngClass]="theme === 'warm' ? 'text-stone-600' : 'text-slate-300'"
                  >
                    {{ item.descriptionSummary }}
                  </p>
                }
              </div>

              <!-- Price & CTA Action -->
              <div class="pt-4 border-t flex flex-wrap items-center justify-between gap-2.5" [ngClass]="theme === 'warm' ? 'border-stone-200' : 'border-slate-700/60'">
                <div class="min-w-0">
                  <p class="text-[10px] uppercase font-semibold opacity-60">Fee per Session</p>
                  <p
                    class="text-base sm:text-lg font-extrabold truncate"
                    [ngClass]="theme === 'warm' ? 'text-amber-800' : (theme === 'bold' ? 'text-red-300 font-black' : 'text-emerald-400')"
                  >
                    @if (item.price && item.price > 0) {
                      LKR {{ item.price | number }} <span class="text-[10px] font-normal opacity-70">/session</span>
                    } @else {
                      Free Enrollment
                    }
                  </p>
                </div>

                <a
                  [routerLink]="['/live-classes', item.id]"
                  class="daisy-btn daisy-btn-sm rounded-xl gap-1.5 shadow-sm shrink-0 font-bold"
                  [ngClass]="theme === 'warm' ? 'daisy-btn-warning bg-amber-600 hover:bg-amber-500 text-white border-none' : (theme === 'bold' ? 'daisy-btn-error bg-red-600 hover:bg-red-500 text-white border-none font-bold' : 'daisy-btn-primary bg-blue-600 hover:bg-blue-500 text-white border-none')"
                >
                  <span>Enroll Now</span>
                  <i class="fa-solid fa-arrow-right text-[11px]"></i>
                </a>
              </div>

            </div>

          </div>
        }
      </div>

      <!-- View All Classes Link -->
      <div class="text-center px-4">
        <a
          routerLink="/live-classes"
          class="daisy-btn daisy-btn-outline daisy-btn-sm sm:daisy-btn-md rounded-2xl gap-2 font-bold px-4 sm:px-8 max-w-full text-xs sm:text-sm shadow-sm"
          [ngClass]="theme === 'warm' ? 'border-amber-600 text-amber-900 hover:bg-amber-600 hover:text-white' : (theme === 'bold' ? 'border-red-500 text-red-300 hover:bg-red-600 hover:text-white' : 'border-blue-500 text-blue-300 hover:bg-blue-600 hover:text-white')"
        >
          <span class="truncate">Explore All Batches & Classes</span>
          <i class="fa-solid fa-arrow-right shrink-0"></i>
        </a>
      </div>
    }

    <!-- Empty State: No live classes published yet -->
    @if (!loading && liveClasses.length === 0) {
      <div
        class="max-w-xl mx-auto p-5 sm:p-8 rounded-2xl sm:rounded-3xl border text-center space-y-3 sm:space-y-4"
        [ngClass]="theme === 'warm' ? 'bg-white border-stone-200 text-stone-800' : 'bg-slate-800/60 border-slate-700 text-slate-200'"
      >
        <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mx-auto flex items-center justify-center text-xl sm:text-2xl" [ngClass]="theme === 'warm' ? 'bg-amber-500/20 text-amber-700' : 'bg-blue-500/20 text-blue-400'">
          <i class="fa-solid fa-calendar-plus"></i>
        </div>
        <h3 class="text-base sm:text-lg font-bold">New Batches Being Scheduled</h3>
        <p class="text-xs sm:text-sm opacity-70 leading-relaxed">
          Upcoming class schedules and syllabus modules for {{ tenantName || 'this academy' }} are currently being prepared. Check back shortly or connect with us on WhatsApp for enrollment inquiries.
        </p>
      </div>
    }

  </div>
</section>
